/**
 * THE COMMAND PALETTE — ⌘K, OR CTRL-K, OR THE BUTTON IN THE RAIL.
 *
 * The owner's measure: "the user should be able to get and do whatever he wants
 * to do in 2 clicks." A rail of ten sections cannot meet that on its own — the
 * third click is always the one that finds the thing. So this is the answer
 * Spotlight gives: one keystroke, type what you want, act.
 *
 * It does two jobs at once and keeps them visibly apart:
 *   GO SOMEWHERE — every screen this person may open, by name.
 *   DO SOMETHING — the tasks he actually starts: take an order, prepare a
 *     dispatch, record a return, draft a quotation.
 *   FIND SOMETHING — anything else he types is handed to the search.
 *
 * Only what the SESSION permits is offered. A task behind a permission he does
 * not hold is not listed greyed out — it is not there, because an action a
 * person can see and never perform is worse than one he never sees.
 *
 * This is interaction, not skin: CTS 1's rail, cards and colours are untouched
 * (D260 struck the macOS styling and expressly kept interaction improvements).
 */
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SCREENS, NAVIGATION } from '../../src/screens/contracts.js';
import { can, type Session } from './api.js';
import { go } from './hooks.js';

interface Entry { kind: 'do' | 'go'; label: string; hint: string; href: string; needs: string; keys: string }

/** The tasks a person STARTS. Each one is a single hop to the screen that does it. */
const TASKS: Entry[] = [
  { kind: 'do', label: 'Take an order',        hint: 'what a customer has asked for',        href: '#/orders/new',     needs: 'order.create',    keys: 'new order sale sell' },
  { kind: 'do', label: 'Prepare a dispatch',   hint: 'build a load in the yard',             href: '#/dispatch/new',   needs: 'challan.create',  keys: 'send out load challan lorry truck' },
  { kind: 'do', label: 'Record a return',      hint: 'empties coming back',                  href: '#/returns/new',    needs: 'challan.create',  keys: 'empty back receive' },
  { kind: 'do', label: 'Draft a quotation',    hint: 'an offer, priced from their list',       href: '#/quotes/new',     needs: 'quote.draft' ,    keys: 'quote offer price' },
  { kind: 'do', label: 'Record a tender',      hint: 'a notice with a deadline',             href: '#/tenders/new',    needs: 'tender.manage',   keys: 'tender bid emd' },
  { kind: 'do', label: 'Add a customer',       hint: 'a new name and one site',              href: '#/customers/new',  needs: 'customer.manage', keys: 'customer new party' },
  { kind: 'do', label: 'Register a cylinder',  hint: 'one, or a whole lot',                  href: '#/cylinders/new',  needs: 'cylinder.register', keys: 'cylinder new serial lot' },
  { kind: 'do', label: 'Raise a purchase order', hint: 'what we are buying',                 href: '#/purchases/new',  needs: 'po.raise'  ,       keys: 'purchase buy supplier po' },
];

export function Palette({ session, open, onClose }: { session: Session; open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const box = useRef<{ focus: () => void } | null>(null);

  const entries = useMemo<Entry[]>(() => {
    const screens: Entry[] = SCREENS
      .filter((s) => s.id !== 'sign-in' && (!s.requires || can(session, s.requires)))
      .map((s) => ({
        kind: 'go' as const, label: s.title.split(' — ')[0] ?? s.title,
        hint: NAVIGATION.find((n) => n.screens.includes(s.id))?.label ?? 'Go to',
        href: `#/${s.id}`, needs: s.requires, keys: `${s.id} ${s.title}`,
      }));
    return [...TASKS.filter((t) => can(session, t.needs)), ...screens];
  }, [session]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return entries.slice(0, 9);
    return entries.filter((e) => `${e.label} ${e.hint} ${e.keys}`.toLowerCase().includes(t)).slice(0, 9);
  }, [q, entries]);

  useEffect(() => { setCursor(0); }, [q]);
  useEffect(() => { if (open) { setQ(''); setTimeout(() => box.current?.focus(), 10); } }, [open]);

  if (!open) return null;

  function choose(e?: Entry) {
    if (e) { go(e.href); onClose(); return; }
    if (q.trim()) { go(`#/find?q=${encodeURIComponent(q.trim())}`); onClose(); }
  }

  return (
    <div className="palette-back" role="presentation" onClick={onClose}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="What do you want to do?"
           onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}>
        <input
          id="palette-input" type="text" value={q} autoComplete="off"
          ref={box as never}
          placeholder="What do you want to do?  —  or type a serial, a challan, a name"
          onChange={(e: { target: { value: string } }) => setQ(e.target.value)}
          onKeyDown={(e: { key: string; preventDefault: () => void }) => {
            if (e.key === 'Escape') { onClose(); return; }
            if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, shown.length - 1)); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); return; }
            if (e.key === 'Enter') { e.preventDefault(); choose(shown[cursor]); }
          }} />
        <div className="palette-list">
          {shown.length === 0 ? (
            <button type="button" className="palette-row on" onClick={() => choose()}>
              <span className="palette-kind find">find</span>
              <span className="grow">Search everything for “{q}”</span>
              <span className="palette-key">enter</span>
            </button>
          ) : shown.map((e, i) => (
            <button type="button" key={e.href + e.label} className={`palette-row${i === cursor ? ' on' : ''}`}
                    onMouseEnter={() => setCursor(i)} onClick={() => choose(e)}>
              <span className={`palette-kind ${e.kind}`}>{e.kind}</span>
              <span className="grow"><strong>{e.label}</strong><span className="muted"> · {e.hint}</span></span>
              {i === cursor ? <span className="palette-key">enter</span> : null}
            </button>
          ))}
          {q.trim() && shown.length > 0 ? (
            <button type="button" className="palette-row quiet" onClick={() => choose()}>
              <span className="palette-kind find">find</span>
              <span className="grow">…or search everything for “{q}”</span>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** ⌘K / Ctrl-K from anywhere, and Escape to leave. */
export function usePalette(): { open: boolean; setOpen: (v: boolean) => void } {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: { key: string; metaKey: boolean; ctrlKey: boolean; preventDefault: () => void }) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); setOpen((v) => !v); }
      if (e.key === 'Escape') setOpen(false);
    };
    globalThis.addEventListener?.('keydown', onKey as never);
    return () => globalThis.removeEventListener?.('keydown', onKey as never);
  }, []);
  return { open, setOpen };
}
